import { Comment as AntdComment, Tooltip, Avatar } from 'antd'
import * as React from 'react'
import { useRef } from 'react'
import moment from 'moment'

import { Comment as IComment } from '../dtos'
import {
    DislikeFilledIcon,
    DislikeIcon,
    LikeFilledIcon,
    LikeIcon,
    FavoriteFilledIcon,
    FavoriteIcon
} from '../../icons'
import { RichTextViewer } from '../../rich-text-editor'

export interface CommentProps {
    comment: IComment
    user: { id: string }
    replyEditor: JSX.Element
    editEditor: JSX.Element
    events?: any
    requestedToShowReplyEditor: (commentId: string) => void
    requestedToShowEditEditor: (commentId: string) => void
    bookmarkComment: (commentId: string) => void
    likeComment: (commentId: string) => void
    dislikeComment: (commentId: string) => void
    onLinkClick?: (url: string) => void
    getTributeComponent: any
    options?: {
        canBookmarkComment?: boolean
        canDislike?: boolean
        canLike?: boolean
    }
}

export const Comment: React.FC<CommentProps> = (props) => {
    const {
        comment,
        user,
        replyEditor,
        editEditor,
        events,
        getTributeComponent,
        requestedToShowReplyEditor,
        requestedToShowEditEditor,
        bookmarkComment,
        likeComment,
        dislikeComment,
        onLinkClick,
        options
    } = props

    const commentRef = useRef(null)

    const bookmark = () => {
        bookmarkComment(comment.id)
    }

    const like = () => {
        likeComment(comment.id)
    }

    const dislike = () => {
        dislikeComment(comment.id)
    }

    const onRequestToReply = () => {
        requestedToShowReplyEditor(comment.id)
    }

    const onRequestToEdit = () => {
        requestedToShowEditEditor(comment.id)
    }

    // handle invalid states
    if (comment.likedBy === undefined) {
        comment.likedBy = []
    }

    if (comment.dislikedBy === undefined) {
        comment.dislikedBy = []
    }

    if (comment.bookmarkedBy === undefined) {
        comment.bookmarkedBy = []
    }

    const isLikedByUser: boolean =
        comment.likedBy.find((u) => u.oauthId === user.id) !== undefined
    const isDislikedByUser: boolean =
        comment.dislikedBy.find((u) => u.oauthId === user.id) !== undefined

    let action: string = ''
    if (isLikedByUser) {
        action = 'liked'
    } else if (isDislikedByUser) {
        action = 'disliked'
    }

    const isBookmarkedByUser: boolean =
        comment.bookmarkedBy.find((u) => u.oauthId === user.id) !== undefined
            ? true
            : false

    const actions = [
        <span>
            <Tooltip
                title={
                    comment.likedBy.length > 0
                        ? comment.likedBy.map((like) => {
                              return <div key={like.id}>{like.display}</div>
                          })
                        : 'Like'
                }
            >
                {action === 'liked' ? (
                    <LikeFilledIcon onClick={like} />
                ) : (
                    <LikeIcon onClick={like} />
                )}
            </Tooltip>
            <span style={{ paddingLeft: 8, cursor: 'auto' }}>
                {comment.likedBy.length}
            </span>
        </span>,
        options?.canDislike && (
            <span>
                <Tooltip
                    title={
                        comment.dislikedBy.length > 0
                            ? comment.dislikedBy.map((dislike) => {
                                  return (
                                      <div key={dislike.id}>
                                          {dislike.display}
                                      </div>
                                  )
                              })
                            : 'Dislike'
                    }
                >
                    {action === 'disliked' ? (
                        <DislikeFilledIcon onClick={dislike} />
                    ) : (
                        <DislikeIcon onClick={dislike} />
                    )}
                </Tooltip>
                <span style={{ paddingLeft: 8, cursor: 'auto' }}>
                    {comment.dislikedBy.length}
                </span>
            </span>
        ),
        <span onClick={onRequestToReply} style={{ paddingLeft: 8 }}>
            Reply
        </span>,
        comment.author && user.id === comment.author.oauthId && (
            <span onClick={onRequestToEdit} style={{ paddingLeft: 8 }}>
                Edit
            </span>
        )
    ]

    const onAuthorClick = () => {
        events && events.onAuthorClick(comment.author.id)
    }

    return (
        <div id={comment.id} ref={commentRef}>
            <AntdComment
                actions={actions}
                author={<a>{comment.author && comment.author.display}</a>}
                avatar={
                    <div onClick={onAuthorClick}>
                        <Avatar
                            size="small"
                            src={comment.author && comment.author.picture}
                            alt={comment.author && comment.author.display}
                        />
                    </div>
                }
                content={
                    editEditor || (
                        <RichTextViewer
                            content={comment.content}
                            getTributeComponent={getTributeComponent}
                            onLinkClick={onLinkClick}
                        />
                    )
                }
                datetime={
                    <div>
                        <Tooltip
                            title={moment(comment.createdAt).format(
                                'YYYY-MM-DD HH:mm:ss'
                            )}
                        >
                            <span>{moment(comment.createdAt).fromNow()}</span>
                        </Tooltip>
                        <Tooltip title="bookmark">
                            {isBookmarkedByUser ? (
                                <FavoriteFilledIcon
                                    onClick={bookmark}
                                    style={{
                                        marginLeft: '16px',
                                        color: isBookmarkedByUser
                                            ? '#fadb14'
                                            : ''
                                    }}
                                />
                            ) : (
                                <FavoriteIcon
                                    onClick={bookmark}
                                    style={{
                                        marginLeft: '16px',
                                        color: isBookmarkedByUser
                                            ? '#fadb14'
                                            : ''
                                    }}
                                />
                            )}
                        </Tooltip>
                        {/* <Rate
              onChange={bookmark}
              style={{
                position: "absolute",
                marginLeft: "16px",
                top: "-2px",
                fontSize: "1.3em"
              }}
              tooltips={["Mark as favorite"]}
              count={1}
            /> */}
                    </div>
                }
            >
                {props.children}
                {replyEditor}
            </AntdComment>
        </div>
    )
}

export interface CreateNewCommentProps {
    comments: IComment[]
    content: string
    parentConversation: { id: string }
    user: { id: string }
    parentComment: { id: string }
}

export const createNewComment = (args: CreateNewCommentProps): any => {
    const { comments, content, parentComment, parentConversation, user } = args

    const comment = {
        author: user,
        content: content,
        parentConversation: parentConversation
    }

    if (!parentComment) {
        return comment
    } else {
        return {
            ...comment,
            parentComment: parentComment
        }
    }
}
